/**
 * <line-item-repeater>
 *
 * Keeps one line item property field per unit in the product form's quantity input,
 * e.g. quantity 3 renders properties[Bag 1], properties[Bag 2] and properties[Bag 3].
 *
 * Row 1 is rendered server side so the field still works with JS disabled. Rows 2..n are
 * cloned from the [data-row-template] <template>, with the literal `__n__` replaced by the
 * row number. Fields live outside the <form> and are associated with it through their
 * `form` attribute, so FormData picks them up on add to cart.
 *
 * Expects:
 *   data-form-id  id of the product form these fields belong to
 *   data-max      maximum number of fields to render
 */
class LineItemRepeater extends HTMLElement {
    constructor() {
        super();
        this.handleQuantityEvent = this.handleQuantityEvent.bind(this);
    }

    connectedCallback() {
        this.rowsContainer = this.querySelector('[data-rows]');
        this.rowTemplate = this.querySelector('[data-row-template]');
        this.formId = this.dataset.formId;
        this.max = parseInt(this.dataset.max, 10) || 1;

        if (!this.rowsContainer || !this.rowTemplate || !this.formId) return;

        // Delegated on document because the quantity selector is a sibling block that may
        // be rendered after this one, and because the theme's own change event is debounced.
        document.addEventListener('input', this.handleQuantityEvent);
        document.addEventListener('change', this.handleQuantityEvent);
        document.addEventListener('on:QuantityChange', this.handleQuantityEvent);

        // Quantity can start above 1 (quantity rules), and the quantity selector is a
        // sibling block that may not be in the DOM yet. A timeout rather than rAF so this
        // still runs when the page is rendered in a background tab.
        setTimeout(() => this.syncRows(), 0);
    }

    disconnectedCallback() {
        document.removeEventListener('input', this.handleQuantityEvent);
        document.removeEventListener('change', this.handleQuantityEvent);
        document.removeEventListener('on:QuantityChange', this.handleQuantityEvent);
    }

    // Only react to the quantity input of our own form. Cart drawer line items and other
    // product forms on the page dispatch the same events.
    handleQuantityEvent(event) {
        const target = event.target;
        if (!target || target.name !== 'quantity') return;
        if (target.getAttribute('form') !== this.formId) return;
        // Only a change the customer just made should move the page under them.
        this.syncRows(target.value, { keepAnchorStill: true });
    }

    get quantityInput() {
        const form = document.getElementById(this.formId);
        const field = form && form.elements ? form.elements.quantity : null;
        // A RadioNodeList is returned if more than one control shares the name.
        if (field instanceof RadioNodeList) return field[0];
        return field || null;
    }

    /**
     * Rows are added above the quantity selector and the add to cart button, so every
     * extra bag pushed both of them down the page — about 93px per bag, which moved the
     * add to cart button out from under the pointer that had just clicked "+".
     *
     * The fix anchors on the control the customer is actually touching: measure where it
     * sits, change the rows, then scroll by however far it moved. The quantity stepper
     * and the button below it stay put, and the new field appears to grow upwards.
     *
     * Not a Core Web Vitals problem either way — layout shifts within 500ms of a user
     * interaction are excluded from CLS — but the jank was real regardless of the score.
     */
    syncRows(value, { keepAnchorStill = false } = {}) {
        const raw = value !== undefined ? value : this.quantityInput && this.quantityInput.value;
        const quantity = Math.min(Math.max(parseInt(raw, 10) || 1, 1), this.max);
        const current = this.rowsContainer.querySelectorAll('[data-row]').length;

        if (quantity === current) return;

        const anchor = keepAnchorStill ? this.quantityInput : null;
        const anchorTop = anchor ? anchor.getBoundingClientRect().top : 0;

        if (quantity > current) {
            let markup = '';
            for (let index = current + 1; index <= quantity; index += 1) {
                markup += this.rowTemplate.innerHTML.replace(/__n__/g, index);
            }
            this.rowsContainer.insertAdjacentHTML('beforeend', markup);
        } else {
            // Trim from the end so anything the customer already typed is kept.
            const rows = this.rowsContainer.querySelectorAll('[data-row]');
            for (let index = rows.length - 1; index >= quantity; index -= 1) {
                rows[index].remove();
            }
        }

        if (!anchor) return;
        const shift = anchor.getBoundingClientRect().top - anchorTop;
        if (!shift) return;
        // Instant, not smooth: this is a correction, not a journey. Any `scroll-behavior:
        // smooth` on the page would otherwise animate it and reintroduce the lurch.
        window.scrollBy({ top: shift, left: 0, behavior: 'instant' });
    }
}

customElements.get('line-item-repeater') ||
    customElements.define('line-item-repeater', LineItemRepeater);
